import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { QuickStockEntryModal } from "@/components/QuickStockEntryModal";
import { QuickPharmacyMovementModal } from "@/components/pharmacy/QuickPharmacyMovementModal";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import { formatMoney } from "@/core/pharmacy/format";
import {
  persistProductPrice,
  resolveProductPrice,
} from "@/core/inventory/pharmacy-stock-sync";
import { getProductStockState } from "@/core/pharmacy/pharmacy-store";
import type { StockProduct } from "@/core/pharmacy/types";
import { useBarcodeScan } from "@/hooks/useBarcodeScan";
import { routes } from "@/router/all_routes";

interface ScanHistoryEntry {
  barcode: string;
  label: string;
  at: string;
}

const SCAN_HISTORY_KEY = "pharmacy.scanHistory";
const MAX_HISTORY = 8;

function readScanHistory(): ScanHistoryEntry[] {
  try {
    const raw = localStorage.getItem(SCAN_HISTORY_KEY);
    return raw ? (JSON.parse(raw) as ScanHistoryEntry[]) : [];
  } catch {
    return [];
  }
}

function pushScanHistory(barcode: string, label: string): void {
  const next: ScanHistoryEntry[] = [
    { barcode, label, at: new Date().toISOString() },
    ...readScanHistory().filter((e) => e.barcode !== barcode),
  ].slice(0, MAX_HISTORY);
  localStorage.setItem(SCAN_HISTORY_KEY, JSON.stringify(next));
}

export function PharmacyStockScanPage() {
  const navigate = useNavigate();
  const [stockQty, setStockQty] = useState<number | null>(null);
  const [iscReservedQty, setIscReservedQty] = useState<number>(0);
  const [priceDraft, setPriceDraft] = useState("");
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [unknownBarcode, setUnknownBarcode] = useState("");
  const [movementMode, setMovementMode] = useState<"receipt" | "issue" | null>(null);
  const [history, setHistory] = useState<ScanHistoryEntry[]>(() => readScanHistory());

  const refreshStock = useCallback(async (productId: string) => {
    try {
      const balances = await pharmacyInventoryApi.listPharmacyStockBalances();
      const row = balances.find((b) => b.productId === productId);
      setStockQty(row?.quantityOnHand ?? 0);
      setIscReservedQty(row?.quantityReserved ?? 0);
    } catch {
      setStockQty(null);
    }
  }, []);

  const onFound = useCallback(
    (found: StockProduct, barcode: string) => {
      pushScanHistory(barcode, found.label);
      setHistory(readScanHistory());
      const saved = resolveProductPrice(found);
      setPriceDraft(saved !== null ? String(saved) : "");
      void refreshStock(found.id);
    },
    [refreshStock],
  );

  const onNotFound = useCallback((barcode: string) => {
    setUnknownBarcode(barcode);
    setCreateModalOpen(true);
    setStockQty(null);
  }, []);

  const {
    scanRef,
    scanValue,
    scanLoading,
    scanStatus,
    scanError,
    apiOffline,
    lastBarcode,
    product,
    setProduct,
    focusScan,
    handleScanChange,
    handleScanKeyDown,
    lookupBarcode,
  } = useBarcodeScan({ onFound, onNotFound });

  useEffect(() => {
    focusScan();
  }, [focusScan]);

  const reservation = useMemo(
    () => (product ? getProductStockState(product.id) : null),
    [product],
  );

  const savePrice = async () => {
    if (!product) return;
    const price = Number(priceDraft);
    if (price <= 0) return;
    await persistProductPrice(product.id, price);
    setProduct({ ...product, sellingPrice: price });
    setSuccessMsg(`Prix enregistré : ${formatMoney(price)} pour « ${product.label} ».`);
    window.setTimeout(() => setSuccessMsg(null), 4000);
  };

  const goToInvoice = () => {
    if (!product) return;
    const params = new URLSearchParams();
    if (product.barcode) params.set("barcode", product.barcode);
    else params.set("productId", product.id);
    navigate(`${routes.pharmacyInvoice}?${params.toString()}`);
  };

  return (
    <div>
      <PageHeader
        title="Scan code-barres"
        description="Lecteur USB ou saisie manuelle — consultation stock, entrées/sorties et facturation."
        actions={
          <div className="d-flex gap-2 ph-page-header-actions">
            <Link to={routes.pharmacyStockArticles} className="btn btn-outline-secondary btn-sm">
              Articles
            </Link>
            <Link to={routes.pharmacyInvoice} className="btn btn-primary btn-sm">
              Nouvelle facture
            </Link>
          </div>
        }
      />

      {successMsg && <div className="alert alert-success py-2">{successMsg}</div>}
      {scanError && (
        <div className="alert alert-warning py-2">
          {scanError}
          {apiOffline && (
            <span className="ms-1 small">
              Lancez <code>pnpm dev:api</code> dans <code>itaas-inventory</code>.
            </span>
          )}
        </div>
      )}

      <div className="row g-3 ph-split-workspace">
        <div className="col-lg-5">
          <div className="ph-invoice-sheet ph-scan-panel">
            <div className="ph-invoice-section-title">Scanner</div>
            <div className="position-relative mb-2">
              <input
                ref={scanRef}
                className={`form-control form-control-lg font-monospace ph-scan-input ${scanLoading ? "pe-5" : ""}`}
                placeholder="Scannez ici…"
                value={scanValue}
                onChange={(e) => handleScanChange(e.target.value)}
                onKeyDown={handleScanKeyDown}
                disabled={scanLoading}
                autoComplete="off"
                aria-label="Code-barres"
              />
              {scanLoading && (
                <span
                  className="position-absolute top-50 end-0 translate-middle-y me-3 ph-scan-spinner"
                  aria-hidden
                />
              )}
            </div>
            <p className="small text-muted mb-0">
              Le champ garde le focus pour les lecteurs USB (mode clavier). Recherche automatique
              dès 4 caractères ou à la touche Entrée.
            </p>
            {scanLoading && <p className="small text-primary mt-2 mb-0">Recherche…</p>}
            {scanStatus && !scanLoading && (
              <p className="small text-success mt-2 mb-0">{scanStatus}</p>
            )}

            {history.length > 0 && (
              <div className="mt-4">
                <div className="ph-invoice-section-title">Derniers scans</div>
                <ul className="list-unstyled mb-0 ph-scan-history">
                  {history.map((entry) => (
                    <li key={entry.barcode}>
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-start p-0 ph-scan-history-btn"
                        onClick={() => lookupBarcode(entry.barcode)}
                      >
                        <code>{entry.barcode}</code>
                        <span className="text-muted ms-2">{entry.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-7">
          <div className="ph-invoice-sheet ph-scan-result h-100">
            <div className="ph-invoice-section-title">Résultat</div>

            {!lastBarcode && !product && (
              <p className="text-muted mb-0">Scannez ou saisissez un code-barres pour commencer.</p>
            )}

            {lastBarcode && !product && !scanLoading && !createModalOpen && (
              <div>
                <div className="alert alert-warning mb-3">
                  Aucun article pour <code>{lastBarcode}</code>.
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setUnknownBarcode(lastBarcode);
                    setCreateModalOpen(true);
                  }}
                >
                  Créer l&apos;article
                </button>
              </div>
            )}

            {product && (
              <div>
                <h3 className="h5 mb-1">{product.label}</h3>
                <div className="small text-muted mb-3">
                  SKU <code>{product.sku}</code>
                  {product.barcode && (
                    <>
                      {" "}
                      · Code <code>{product.barcode}</code>
                    </>
                  )}
                  {" "}
                  · Unité {product.unit}
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-6 col-sm-4">
                    <div className="ph-scan-stat">
                      <span className="ph-scan-stat-label">En stock</span>
                      <span className="ph-scan-stat-value">
                        {stockQty === null ? "—" : stockQty}
                      </span>
                    </div>
                  </div>
                  <div className="col-6 col-sm-4">
                    <div className="ph-scan-stat">
                      <span className="ph-scan-stat-label">Réservé (facture)</span>
                      <span className="ph-scan-stat-value">
                        {Math.max(reservation?.reservedQty ?? 0, iscReservedQty)}
                      </span>
                    </div>
                  </div>
                  <div className="col-6 col-sm-4">
                    <div className="ph-scan-stat">
                      <span className="ph-scan-stat-label">Prix officine</span>
                      <span className="ph-scan-stat-value">
                        {resolveProductPrice(product) !== null
                          ? formatMoney(resolveProductPrice(product)!)
                          : "—"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
                  <div style={{ minWidth: "8rem" }}>
                    <label className="form-label small mb-1">Prix unitaire</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control form-control-sm"
                      value={priceDraft}
                      onChange={(e) => setPriceDraft(e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={savePrice}
                    disabled={!priceDraft || Number(priceDraft) <= 0}
                  >
                    Enregistrer prix
                  </button>
                </div>

                <div className="d-flex flex-wrap gap-2 ph-split-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={() => setMovementMode("receipt")}
                  >
                    Entrée stock
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setMovementMode("issue")}
                  >
                    Sortie stock
                  </button>
                  <button type="button" className="btn btn-sm btn-primary" onClick={goToInvoice}>
                    Facturer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <QuickStockEntryModal
        open={createModalOpen}
        barcode={unknownBarcode}
        purpose="stock"
        onClose={() => {
          setCreateModalOpen(false);
          focusScan();
        }}
        onCreated={(created) => {
          setProduct(created);
          setPriceDraft(String(resolveProductPrice(created) ?? ""));
          pushScanHistory(unknownBarcode || created.barcode || created.code, created.label);
          setHistory(readScanHistory());
          void refreshStock(created.id);
          setSuccessMsg(`Article « ${created.label} » créé et entré en stock.`);
          window.setTimeout(() => setSuccessMsg(null), 4000);
        }}
      />

      {movementMode && product && (
        <QuickPharmacyMovementModal
          open
          mode={movementMode}
          product={product}
          onClose={() => setMovementMode(null)}
          onSuccess={(message) => {
            setSuccessMsg(message);
            void refreshStock(product.id);
            window.setTimeout(() => setSuccessMsg(null), 4000);
            focusScan();
          }}
        />
      )}
    </div>
  );
}
